import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { Button, Field } from "../components/ui";
import SignOff from "../components/SignOff";
import PhotoButton from "../components/PhotoButton";
import { useAuth } from "../auth";
import { apiGet } from "../api";
import { submitLog } from "../offline";
import { getDeviceId } from "../deviceId";
import { getSticky, setSticky } from "../sticky";
import { C, R, S } from "../theme";

const WIND_LIMIT = 24;

export default function WindCheck() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [attractions, setAttractions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [speed, setSpeed] = useState("");
  const [signoff, setSignoff] = useState({ signature: null, name: user?.display_name || user?.company_name || "", initials: "", complete: false });
  const [loc, setLoc] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [alert, setAlert] = useState(false);
  const [loaded, setLoaded] = useState(false);


  useEffect(() => {
    apiGet("/attractions").then((a) => { const inf = a.filter((x) => x.category === "inflatable"); setAttractions(inf); setSelectedIds(inf.map((x) => x.id)); setLoaded(true); });
  }, []);

  const targetIds = selectAll ? attractions.map((a) => a.id) : selectedIds;
  const toggleOne = (id) => { setSelectedIds((prev) => { const base = selectAll ? [] : prev; return base.includes(id) ? base.filter((x) => x !== id) : [...base, id]; }); setSelectAll(false); };

  const limitFor = (a) => Number(a.max_wind_mph) || WIND_LIMIT;
  const targeted = attractions.filter((a) => targetIds.includes(a.id));
  const effLimit = targeted.length ? Math.min(...targeted.map(limitFor)) : WIND_LIMIT;

  const windNum = parseFloat(speed);
  const isHigh = !isNaN(windNum) && windNum >= effLimit;

  const submit = async () => {
    if (submitting.current) return;
    if (!signoff.complete || targetIds.length === 0) return;
    submitting.current = true;
    setBusy(true);
    try {
      const deviceId = getDeviceId();
      if (loc) setSticky("location", loc);
      for (const aid of targetIds) await submitLog({ attraction_id: aid, log_type: "wind_check", results: [], signature: signoff.signature, signed_by_name: signoff.name, signed_by_initials: signoff.initials, device_id: deviceId, wind_speed: windNum, location: loc || null, photos });
      if (isHigh) setAlert(true); else nav(-1);
    } finally { submitting.current = false; setBusy(false); }
  };

  const chip = (active) => ({ height: 40, display: "flex", alignItems: "center", gap: 6, padding: `0 ${S.md}px`, borderRadius: R.pill, background: active ? C.amber : C.card, border: `1px solid ${active ? C.amber : C.border}`, color: active ? C.black : C.sub, fontWeight: 700, flexShrink: 0 });

  return (
    <div className="fade-up">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: S.lg }}>
        <button data-testid="wind-back" className="pressable" onClick={() => nav(-1)}><Icon name="close" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>⚡ Quick Wind Check</div>
        <div style={{ width: 28 }} />
      </div>
      <div style={{ padding: S.lg }}>
        {loaded && attractions.length === 0 ? (
          <div style={{ textAlign: "center", padding: `${S.xxxl}px 0` }}>
            <Icon name="balloon-outline" size={56} color={C.muted} />
            <div style={{ color: C.text, fontSize: 20, fontWeight: 800, marginTop: S.md }}>No inflatables added</div>
            <div style={{ color: C.muted, fontSize: 15, marginTop: S.sm, lineHeight: 1.4 }}>Wind checks only apply to inflatables / bouncy castles. Add one as an attraction first.</div>
            <div style={{ height: S.lg }} /><Button title="Go Back" testID="wind-empty-back" variant="dark" onPress={() => nav(-1)} />
          </div>
        ) : step === 1 ? (
          <>
            <div style={{ color: C.amber, fontWeight: 800, letterSpacing: 1, fontSize: 12 }}>STEP 1 OF 2</div>
            <div className="display" style={{ color: C.text, fontSize: 28, fontWeight: 900, marginTop: S.xs, marginBottom: S.lg }}>Current wind speed?</div>
            {attractions.length >= 1 && (
              <>
                <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" }}>Apply this reading to</div>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: S.sm }}>Defaults to all devices — tap a specific device to limit it to one.</div>
                <div style={{ display: "flex", gap: S.sm, overflowX: "auto", paddingBottom: 4, marginBottom: S.sm }}>
                  <button data-testid="wind-attr-all" onClick={() => setSelectAll(true)} style={chip(selectAll)}><Icon name={selectAll ? "checkmark-circle" : "cube"} size={16} color={selectAll ? C.black : C.sub} /> All devices ({attractions.length})</button>
                  {attractions.map((a) => <button key={a.id} data-testid={`wind-attr-${a.id}`} onClick={() => toggleOne(a.id)} style={chip(!selectAll && selectedIds.includes(a.id))}>{a.name}</button>)}
                </div>
              </>
            )}
            <div style={{ display: "flex", alignItems: "center", background: C.card, borderRadius: R.lg, border: `2px solid ${isHigh ? C.red : C.borderStrong}`, padding: `0 ${S.lg}px` }}>
              <input data-testid="wind-speed-input" inputMode="decimal" value={speed} onChange={(e) => setSpeed(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="0" style={{ fontSize: 56, fontWeight: 900, textAlign: "center", flex: 1, background: "transparent", border: "none", color: C.text, minHeight: 110, outline: "none" }} />
              <span style={{ color: C.muted, fontSize: 28, fontWeight: 800 }}>mph</span>
            </div>
            {isHigh && <div style={{ display: "flex", alignItems: "center", gap: S.sm, background: C.red, borderRadius: R.md, padding: S.md, marginTop: S.md }}><Icon name="warning" size={20} color={C.text} /><span style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>Exceeds safe limit ({effLimit} mph)</span></div>}
            {!!speed && targeted.length > 0 && (
              <div data-testid="wind-limit-breakdown" style={{ marginTop: S.md, background: C.card, borderRadius: R.md, border: `1px solid ${C.border}`, overflow: "hidden" }}>
                <div style={{ color: C.sub, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, padding: `${S.sm}px ${S.md}px`, borderBottom: `1px solid ${C.border}` }}>Per-attraction wind limit</div>
                {targeted.map((a) => {
                  const lim = limitFor(a); const over = !isNaN(windNum) && windNum >= lim;
                  return (
                    <div key={a.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${S.sm}px ${S.md}px`, borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>{a.name}</span>
                      <span style={{ color: over ? C.red : C.green, fontSize: 13, fontWeight: 800 }}>{over ? `EXCEEDS ${lim} mph` : `OK · limit ${lim} mph`}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ height: S.lg }} />
            <Field label="Location of this check (optional)" testID="wind-location" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. North field, Pitch 3" />
            <div style={{ marginTop: S.md }}>
              <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Anemometer photo (optional)</div>
              {photos.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: S.sm, marginBottom: S.sm }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: "relative" }}>
                      <img src={p} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: R.sm, border: `1px solid ${C.borderStrong}` }} />
                      <button data-testid={`remove-wind-photo-${i}`} onClick={() => setPhotos((a) => a.filter((_, idx) => idx !== i))} style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: 11, background: C.red, border: "none", color: C.text, fontWeight: 900, lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <PhotoButton value={null} onCapture={(p) => setPhotos((a) => [...a, p])} allowGallery testID="wind-photo" />
            </div>
            <Button title="Next: Sign" testID="wind-next" disabled={!speed || targetIds.length === 0} onPress={() => setStep(2)} style={{ marginTop: S.md }} />
          </>
        ) : (
          <>
            <div style={{ color: C.amber, fontWeight: 800, letterSpacing: 1, fontSize: 12 }}>STEP 2 OF 2</div>
            <div className="display" style={{ color: C.text, fontSize: 28, fontWeight: 900, marginTop: S.xs, marginBottom: S.lg }}>Operator sign-off</div>
            <SignOff defaultName={signoff.name} onChange={setSignoff} />
            <Button title={isHigh ? "Submit High-Wind Log" : "Submit Wind Log"} testID="wind-submit" variant={isHigh ? "red" : "green"} disabled={!signoff.complete} loading={busy} onPress={submit} style={{ marginTop: S.lg }} />
            <button className="pressable" onClick={() => setStep(1)} style={{ display: "block", margin: "12px auto 0", color: C.muted, fontWeight: 700 }}>Back</button>
          </>
        )}
      </div>
      {alert && (
        <div className="modal-backdrop modal-center" style={{ background: "rgba(0,0,0,0.85)" }}>
          <div style={{ background: C.red, borderRadius: R.lg, padding: S.xl, textAlign: "center", width: "100%", maxWidth: 400 }}>
            <Icon name="warning" size={64} color={C.text} />
            <div className="display" style={{ color: C.text, fontSize: 34, fontWeight: 900, marginTop: S.sm }}>🚨 WARNING</div>
            <div style={{ color: C.text, fontSize: 18, fontWeight: 800, marginTop: S.md, lineHeight: 1.4 }}>SAFE WIND SPEED EXCEEDED ({effLimit}+ MPH). DEFLATE AND SECURE ATTRACTION IMMEDIATELY.</div>
            <div style={{ height: S.lg }} /><Button title="I have secured the attraction" testID="wind-alert-ack" variant="dark" onPress={() => { setAlert(false); nav(-1); }} />
          </div>
        </div>
      )}
    </div>
  );
}
